import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'reactstrap';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';
import BookYourStayContainer from 'containers/BookYourStayContainer';
import WaitlistFormContainer from 'routes/DateSelection/containers/WaitlistFormContainer';
import Well from 'components/Well';
import Button from 'components/Button';
import Select from 'components/Select';
import GuestDetailsContainer from 'containers/GuestDetailsContainer';
import { isMobileUserAgent } from 'utils/mobile';
import { getBookingEnginePath } from 'utils/routeUtils';
import { BOOKING_ENGINE_BASE_PATH } from 'utils/constants';

const CommentsWell = styled(Well)`
  flex: 0 0 20%;
  max-height: 20%;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
    max-height: 35%;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    height: 100%;
    max-height: 100%;
  }

  .book-your-stay {
    margin-bottom: 0;
    flex: 0 0 28%;
    max-height: 28%;
    
    ${ifProp(
      'isHostBooking',
      css`
        @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
          flex: 0 0 24%;
          max-height: 24%;
        }
      `
    )}

    @media screen and (max-width: ${prop('theme.breakpoints.medium')}) {
      max-height: 48%;
    }
  }

  .nav-item {
    cursor: pointer;
  }

  .guest-info-container {
    padding: 0;
    flex: 0 0 auto;
    min-height: 15rem;

    @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
      flex: 0 0 45%;
      min-height: 45%;
      max-height: 45%;      
    }
  }

  ${CommentsWell}:first-child {
    margin-top: 1em;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  resize: none;
  border: 0.0625em solid ${props => props.theme.colors.black};
`;

const WaitlistWell = styled(Well)`
  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    display: none;
  }

  ${Select} {
    border: 1px solid ${props => props.theme.colors.black};
  }

  ${WaitlistFormContainer} {
    .row {
      margin: 1em 0 0 0;
    }

    label {
      color: ${props => props.theme.h5.color};
    }
  }
`;

const propTypes = {
  bookingComments: PropTypes.string,
  onNextClick: PropTypes.func,
  errors: PropTypes.array,
  errorPopoverOpen: PropTypes.bool,
  closeErrorPopover: PropTypes.func,
  setBookingComments: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  isHostBooking: PropTypes.bool,
  hideGuestInfo: PropTypes.bool,
  hideBookYourStay: PropTypes.bool,
  inMobileApp: PropTypes.bool
};

const defaultProps = {
  onNextClick: null,
  errors: [],
  errorPopoverOpen: false,
  closeErrorPopover: null,
  isAdmin: false,
  isHostBooking: false,
  hideGuestInfo: false,
  hideBookYourStay: false,
  inMobileApp: false
};

function HostBookingDetails({
  bookingComments,
  setBookingComments,
  onNextClick,
  errors,
  errorPopoverOpen,
  closeErrorPopover,
  isAdmin,
  isHostBooking,
  hideGuestInfo,
  hideBookYourStay,
  inMobileApp
}) {
  const [waitlistRequestIsOpen, setWaitlistRequestIsOpen] = useState(false);
  const isMobile = isMobileUserAgent();

  const toggleWaitlistRequest = () =>
    setWaitlistRequestIsOpen(!waitlistRequestIsOpen);
  const pathName = getBookingEnginePath(window.location.pathname).trim();
  const isDateSelection = pathName === BOOKING_ENGINE_BASE_PATH;

  return (
    <Wrapper isHostBooking={isHostBooking}>
      {!hideGuestInfo && !inMobileApp && <GuestDetailsContainer />}
      {isMobile && isDateSelection && (
        <WaitlistWell>
          <Button block onClick={toggleWaitlistRequest}>
            Request Waitlist
          </Button>
          <Collapse isOpen={waitlistRequestIsOpen}>
            <WaitlistFormContainer />
          </Collapse>
        </WaitlistWell>
      )}
      <CommentsWell>
        <h5>Comments</h5>
        <TextArea
          value={bookingComments || ''}
          onChange={e => setBookingComments(e.target.value)}
        />
      </CommentsWell>
      {!hideBookYourStay && (
        <BookYourStayContainer
          errors={errors}
          errorPopoverOpen={errorPopoverOpen}
          closeErrorPopover={closeErrorPopover}
          onNextClick={onNextClick}
          isAdmin={isAdmin}
          inMobileApp={inMobileApp}
        />
      )}
    </Wrapper>
  );
}

HostBookingDetails.propTypes = propTypes;
HostBookingDetails.defaultProps = defaultProps;

export default HostBookingDetails;
